// DNI-specific tracking events for GA4/GTM

import { type DNIContext } from './config';

/**
 * Push to GTM dataLayer
 */
const pushToDataLayer = (eventName: string, data: Record<string, unknown>): void => {
  if (typeof window === 'undefined') return;
  
  (window as any).dataLayer = (window as any).dataLayer || [];
  (window as any).dataLayer.push({
    event: eventName,
    ...data,
  });
};

/**
 * Track click-to-call events
 */
export const trackClickCall = (
  context: DNIContext | null,
  source: string,
  phoneNumber: string
): void => {
  const eventData = {
    phone_number: phoneNumber,
    click_source: source,
    city_detected: context?.city || null,
    utm_source: context?.source || null,
    utm_medium: context?.medium || null,
    utm_campaign: context?.campaign || null,
    gclid: context?.gclid || null,
    landing_page: context?.landingPage || window?.location?.pathname || null,
    conversion_type: 'phone_click',
    conversion_value: 150,
    currency: 'USD',
  };
  
  pushToDataLayer('click_call', eventData);
  
  // Meta Pixel
  if (typeof (window as any).fbq === 'function') {
    (window as any).fbq('track', 'Contact', {
      content_category: 'phone_call',
      content_name: source,
    });
  }
  
  console.log('[DNI Tracking] click_call', eventData);
};

/**
 * Track click-to-text/SMS events
 */
export const trackClickText = (
  context: DNIContext | null,
  source: string,
  phoneNumber: string
): void => {
  const eventData = {
    phone_number: phoneNumber,
    click_source: source,
    city_detected: context?.city || null,
    utm_source: context?.source || null,
    utm_campaign: context?.campaign || null,
    landing_page: context?.landingPage || window?.location?.pathname || null,
    conversion_type: 'text_click',
  };
  
  pushToDataLayer('click_text', eventData);
  
  console.log('[DNI Tracking] click_text', eventData);
};

/**
 * Track form submissions with DNI context
 */
export const trackFormSubmit = (
  context: DNIContext | null,
  formType: string,
  additionalData: Record<string, unknown> = {}
): void => {
  const eventData = {
    form_type: formType,
    assigned_tracking_number: context?.assignedNumber || null,
    city_detected: context?.city || null,
    utm_source: context?.source || null,
    utm_medium: context?.medium || null,
    utm_campaign: context?.campaign || null,
    utm_term: null,
    utm_content: null,
    gclid: context?.gclid || null,
    landing_page: context?.landingPage || window?.location?.pathname || null,
    conversion_type: 'lead_form',
    conversion_value: 100,
    currency: 'USD',
    ...additionalData,
  };
  
  pushToDataLayer('form_submit', eventData);
  
  // Meta Pixel
  if (typeof (window as any).fbq === 'function') {
    (window as any).fbq('track', 'Lead', {
      content_category: formType,
      value: 100,
      currency: 'USD',
    });
  }
  
  console.log('[DNI Tracking] form_submit', eventData);
};

/**
 * Track phone number assignment (called automatically by router)
 */
export const trackPhoneAssigned = (context: DNIContext): void => {
  const eventData = {
    assigned_number: context.assignedNumber,
    city_detected: context.city,
    utm_source: context.source,
    utm_medium: context.medium,
    utm_campaign: context.campaign,
    gclid: context.gclid,
    landing_page: context.landingPage,
  };
  
  pushToDataLayer('phone_number_assigned', eventData);
  
  console.log('[DNI Tracking] phone_number_assigned', eventData);
};
